/**
 * @file Stats effectives d'une arme equipee (degats, intervalle, portee, rayon, plombs, dispersion) avec cache invalide par version des stats.
 */
import { Decimal } from '@shared/bignum';
import type { ModeId } from '@shared/enums';
import type { Uid } from '@shared/types';
import type { BehaviorParams, WeaponDef } from '@config/types';
import type { GameConfig, ConfigIndex } from '@config/index';
import { effectiveFireRate } from '../balance/formulas';
import type { PlayerData } from '../state/PlayerData';
import type { StatAggregator } from '../stats/StatAggregator';

export interface ResolvedWeapon {
  uid: Uid;
  slot: number;
  def: WeaponDef;
  params: BehaviorParams;
  /** Degats par projectile (avant critique), stats damage + etoiles du slot + surplus de cadence inclus. */
  damage: Decimal;
  /** Intervalle effectif entre deux tirs (>= dt). */
  interval: number;
  range: number;
  /** Rayon d'explosion monde (explosive/missile/blastSniper), 0 sinon. */
  explosionRadius: number;
  /** Nombre de plombs (shotgun), 1 sinon. */
  pellets: number;
  /** Cone de dispersion max (deg) en tir continu. */
  spreadMaxDeg: number;
  critChance: number;
  critDamage: number;
  /** DPS theorique moyen (crit en esperance, tous projectiles touchent) : UI + hors-ligne. */
  dps: Decimal;
}

export const resolveWeapon = (
  cfg: GameConfig,
  def: WeaponDef,
  uid: Uid,
  slot: number,
  stats: StatAggregator,
  mode: ModeId,
  excludeBuffs = false,
): ResolvedWeapon => {
  const params = cfg.behaviors[def.behavior];
  const q = { mode, behavior: def.behavior, slot, excludeBuffs };
  const speed = stats.getNumber('attackSpeed', q);
  const rate = effectiveFireRate(def.interval, speed, cfg.sim.dt, cfg.balance.combat.overflowSpeedToDamage);
  const damage = new Decimal(def.baseDamage).times(stats.get('damage', q)).times(rate.damageMult);
  const usesRadius = def.behavior === 'explosive' || def.behavior === 'missile' || def.behavior === 'blastSniper';
  const pellets = def.behavior === 'shotgun' ? params.pelletsBase + (params.pelletsFromParam ? def.param : 0) : 1;
  const pelletRatio = def.behavior === 'shotgun' ? params.pelletDamageRatio : 1;
  const spreadMaxDeg = def.behavior === 'single' && def.param > 0 ? def.param * params.spreadParamScale : params.spreadMaxDeg;
  const critChance = Math.min(1, stats.getNumber('critChance', q));
  const critDamage = stats.getNumber('critDamage', q);
  const perShot = damage.times(pellets * pelletRatio).times(1 + critChance * (critDamage - 1));
  return {
    uid,
    slot,
    def,
    params,
    damage: damage.times(pelletRatio),
    interval: rate.interval,
    range: params.range * stats.getNumber('range', q),
    explosionRadius: usesRadius ? def.param * params.explosionRadiusScale : 0,
    pellets,
    spreadMaxDeg,
    critChance,
    critDamage,
    dps: perShot.div(rate.interval),
  };
};

/** Cache par slot, recalcule si la version de l'agregateur, l'arme equipee ou le mode change. */
export class WeaponStatsCache {
  private cache: Array<{ key: string; value: ResolvedWeapon } | undefined> = [];

  constructor(
    private readonly cfg: GameConfig,
    private readonly index: ConfigIndex,
    private readonly stats: StatAggregator,
  ) {}

  forSlot(data: PlayerData, slot: number, mode: ModeId): ResolvedWeapon | null {
    const uid = data.weapons.equipped[slot] ?? null;
    if (!uid) return null;
    const item = data.weapons.items.find((w) => w.uid === uid);
    if (!item) return null;
    this.stats.get('damage'); // force la reconstruction eventuelle (met a jour version)
    const key = `${this.stats.version}|${uid}|${item.defId}|${mode}`;
    const hit = this.cache[slot];
    if (hit && hit.key === key) return hit.value;
    const value = resolveWeapon(this.cfg, this.index.weapon(item.defId), uid, slot, this.stats, mode);
    this.cache[slot] = { key, value };
    return value;
  }

  /** DPS total de l'equipe (somme des slots equipes). `excludeBuffs` : calcul hors-ligne (non cache). */
  teamDps(data: PlayerData, mode: ModeId, excludeBuffs = false): Decimal {
    let total = new Decimal(0);
    for (let s = 0; s < data.weapons.equipped.length; s++) {
      if (!excludeBuffs) {
        const w = this.forSlot(data, s, mode);
        if (w) total = total.plus(w.dps);
        continue;
      }
      const uid = data.weapons.equipped[s];
      const item = uid ? data.weapons.items.find((w) => w.uid === uid) : undefined;
      if (uid && item) total = total.plus(resolveWeapon(this.cfg, this.index.weapon(item.defId), uid, s, this.stats, mode, true).dps);
    }
    return total;
  }

  clear(): void {
    this.cache = [];
  }
}
